import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';

// API/Components
import axiosInstance from '../components/axios';
import useHotkeyHandlers from '../components/hooks';
import SystemDischargeTrendReport from '../components/system_discharge_trend_report';
import SystemDischargeTrendGraph from '../components/system_discharge_trend_graph';
import { parseDateTime, formatDateTime } from '../components/enums';

// Material UI
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { styled } from '@mui/material/styles';

const DataSwitch = styled(Switch)(({ theme }) => ({
	width: 30,
	height: 18,
	padding: 0,
	display: 'flex',
	'& .MuiSwitch-switchBase': {
		padding: 2,
		'&.Mui-checked': {
			transform: 'translateX(14px)',
			color: '#fff',
			'& + .MuiSwitch-track': {
				opacity: 1,
				backgroundColor: '#B1282E',
			},
		},
	},
	'& .MuiSwitch-thumb': {
		boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
		width: 12,
		height: 14,
		borderRadius: 2,
		transition: theme.transitions.create(['width'], {
			duration: 200,
		}),
	},
	'& .MuiSwitch-track': {
		borderRadius: 4,
		opacity: 1,
		backgroundColor: '#0F1820',
		boxSizing: 'border-box',
	},
}));

// System Trend sub-page rendering (Location Name, Title)
export default function SystemDischargeTrend() {
	const { locationNum, eventDateTime } = useParams();
	const [ searchParams ] = useSearchParams();

	// State Management
	const [locationInfo, setLocationInfo] = useState({locationName: ''});

	const [trendData, setTrendData] = useState({});
	
	const [isLoading, setIsLoading] = useState(true);
	const [graphDisplay, setGraphDisplay] = useState(false);
	const [graphType, setGraphType] = useState(1);
	
	const graphTypeRef = useRef();
	const eventDateObj = parseDateTime(eventDateTime);

	// Handle the current graphType for hotkey nav
	useEffect(() => {
		graphTypeRef.current = graphType;
	}, [graphType]);

	// Handle keypresses for graph navigation
	useHotkeyHandlers([
		{
			key: 'F8',
			down: (e) => {
				e.preventDefault();
				switch(graphTypeRef.current) {
					case 0:  // Voltage/Current/Power -> Voltage
						setGraphType(1);
						break;
					case 1:  // Voltage -> Current
						setGraphType(2);
						break;
					case 2:  // Current -> Temperature
						setGraphType(3);
						break;
					case 3:  // Temperature -> Voltage/Current/Power
						setGraphType(0);
						break;
					default:
						setGraphType(0);
						break;
				}
			},
			up: () => {},
		},
	]);
	
	// Get information about the location id specified (name, size, num of temps)
	useEffect(() => {
		axiosInstance.get(`/loc/${locationNum}/size/`).then(response => {
			const locationInfo = response.data;
			setLocationInfo({
				locationName: locationInfo.location_name,
				serialNumber: locationInfo.serial_number,
			});
		});
	}, [locationNum]);
	
	// Get the list of trend data for the specified location id
	useEffect(() => {
		setIsLoading(true);

		// Get System Trend Data
		axiosInstance.get(`/loc/${locationNum}/discharges/${eventDateTime}/system`).then(response => {				
			const trendData = response.data;
			setTrendData(trendData);		
		});

		setIsLoading(false);
	}, [locationNum, eventDateTime]);

	// Event Handlers
	const handleGraphTypeChange = (event) => {
		setGraphType(Number(event.target.value));
	}
	
	const handleViewChange = (event) => {
		setGraphDisplay(event.target.checked);
	}
		
	return (
		<Paper
			padding='true'
			sx={{
				overflow: 'auto',
				boxShadow: '0',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Typography
				variant='h5'
				fontFamily='Saira, sans-serif'
				fontWeight='600'
				color='#0F1820'
				align='center'
				padding='5px'
			>
				{ locationInfo.locationName } (SN {locationInfo.serialNumber})
			</Typography>
			<Toolbar>
				<Grid container spacing={1} alignItems="center">
					<Grid item xs alignItems="left">
						<Typography
							align="left"
							fontFamily='Saira,sans-serif'
							fontWeight='600'
							sx={{ flexGrow: 1}}
						>
							System Discharge Trend: {formatDateTime(eventDateObj)}
						</Typography>
					</Grid>
					{graphDisplay &&
						<Grid item>
							<FormControl sx={{ m: 1, minWidth: 100 }}>
								<label id="graph-type-select" style={{fontFamily:"Saira, sans-serif"}}>Graph Type</label>
								<select
									id="graph-type-select"
									value={graphType}
									label="Graph Type"
									onChange={handleGraphTypeChange}
								>
									<option key={0} value={0}>Power</option>
									<option key={1} value={1}>Voltage</option>
									<option key={2} value={2}>Current</option>
									<option key={3} value={3}>Temperature</option>
								</select>
							</FormControl>
						</Grid>
					}
					<Grid item>
						<Tooltip title="Data display type">
							<Stack direction="row" spacing={1} alignItems="center">
								<Typography fontFamily='Saira, sans-serif'>Report</Typography>
									<DataSwitch checked={graphDisplay} onChange={handleViewChange} disabled={isLoading}/>
								<Typography fontFamily='Saira, sans-serif'>Graph</Typography>
							</Stack>
						</Tooltip>
					</Grid>
				</Grid>
			</Toolbar>
			{ graphDisplay ?
				<SystemDischargeTrendGraph
					trendData={trendData}
					graphType={graphType}
					loading={isLoading}
				/>
			:
				<SystemDischargeTrendReport
					trendData={trendData}
					loading={isLoading}
				/>
			}
		</Paper>
	);
}